import { useEffect, useReducer, useState } from "react";
import { CXButton, CXDropDown, CXTextArea, CXTextInput } from "../../../components/CXForm";
import { CRow, CCol, CTable, CTableHead, CTableHeaderCell, CTableRow, CTableBody, CTableDataCell } from "@coreui/react-pro";
import { useLocation } from "react-router-dom";
import { DATA_CONFIGURATION_PATHS } from "../../../constants/path";
import { FUEL_SUPPLIER_ACTIONS } from "../../../constants/FuelSupplierConstants";
import Access from "../../../components/Access";
import { AccessType } from "../../../constants/roleAccessConstant";
import { IFuelSupplier, IFuelSupplierPayload } from "../../../interfaces/fuelSupplier";
import FuelSupplierTable from "./FuelSupplierTable";
import { activateFuelSupplier, deactivateFuelSupplier, getFuelSuppliers, postCreateNewSupplier, updateFuelSupplier } from "../../../services/fuelSupplier/fuelSupplierAPI";
import { Modal } from "react-bootstrap";
import { initialNewFuelSupplierForm } from "../../../slice/dispatcherSlice";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectUser } from "../../../slice/userSlice";
import { selectAuth } from "../../../slice/authSlice";
import { pushToast } from "../../../slice/appSlice";
import { getPorts } from "../../../services/portAPI";
import { checkIfHasAccess } from "../../../utils/roleAccessHelper";
import NotFoundPage from "../../NotFoundPage";

const FuelSupplierConfigPage = () => {
  const { pathname } = useLocation();
  const isFuelSupplier = pathname === DATA_CONFIGURATION_PATHS.fuelSupplier;
  const { userId } = useAppSelector(selectUser);
  const { userGroupIDs } = useAppSelector(selectAuth);
  const dispatch = useAppDispatch();
  const [displayFuelSuppliers, setDisplayFuelSuppliers] = useState<IFuelSupplier[]>([]);
  const [fuelSuppliers, setFuelSuppliers] = useState<IFuelSupplier[]>([]);
  const [editFuelSupplier, setEditFuelSupplier] = useState<IFuelSupplier | null>(null);
  const [isNewFuelSupplierModal, setIsNewFuelSupplierModal] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [portOptions, setPortOptions] = useState([
    {
      text: "ALL",
      value: "ALL",
    },
  ]);
  const [allPortOptions, setAllPortOptions] = useState([ ]);
  const [airportIATA, setAirportIATA] = useState<string>("ALL");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  //Create new fuel supplier Modal
  const [newFuelSupplierForm, setNewFuelSupplierForm] =
    useState<IFuelSupplierPayload>({
      ...initialNewFuelSupplierForm,
      UpdatedBy: userId,
    });

  //Edit fuel supplier Modal
  const [editFuelSupplierForm, setEditFuelSupplierForm] =
    useState<IFuelSupplierPayload>({
      ...initialNewFuelSupplierForm,
      UpdatedBy: userId,
    });

  useEffect(() => {
    getFuelSuppliersData(searchText);
  }, []);

  useEffect(() => {
    const getAllPorts = async () => {
      try {
        const payload = { airline: 'CPA' };
        const result = await getPorts(payload);
        const formattedPorts = result?.ports.map((port) => ({
          text: port.iata as string,
          value: port.iata as string,
        }));
        const sortedPorts = formattedPorts.sort((a, b) => {
          const aIsAlpha = /^[A-Za-z]/.test(a.text);
          const bIsAlpha = /^[A-Za-z]/.test(b.text);

          return aIsAlpha === bIsAlpha
            ? a.text.localeCompare(b.text)
            : aIsAlpha ? -1 : 1;
        });
        setAllPortOptions(sortedPorts);
      } catch (error) {
        console.error("Error fetching ports:", error);
      }
    };

    getAllPorts();
  }, []);

  const sortByLocale = (key: string) => {
    // Only allow sorting for Airport IATA column
    if (key !== "Airport IATA") {
      return;
    }

    const newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newSortOrder);

    const sortedList = [...displayFuelSuppliers].sort((a, b) => {
      const aValue = a.AirportIATA || "";
      const bValue = b.AirportIATA || "";
      // Handle empty values - show them at the end in both ascending and descending order
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;

      // Both have values, sort alphabetically based on order
      if (newSortOrder === "asc") {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

    // Move items with State = 1 (inactive) to the end while preserving existing sort order
    const activeItems = sortedList.filter(item => item.State === 0 || item.State == null);
    const inactiveItems = sortedList.filter(item => item.State === 1);
    const finalSortedList = [...activeItems, ...inactiveItems];

    setDisplayFuelSuppliers(finalSortedList);
  };

  const resetFuelSupplierForm = () => {
    setNewFuelSupplierForm({
      ...initialNewFuelSupplierForm,
      UpdatedBy: userId,
    });
  };

  const fuelSupplierReducer = (state, action) => {
    switch (action.type) {
      case FUEL_SUPPLIER_ACTIONS.SHOW_EDIT:
        setEditFuelSupplier(action.fuelSupplierItem);
        return {
          ...state,
          isEditBoxVisible: true,
        };
      case FUEL_SUPPLIER_ACTIONS.SHOW_DEACTIVATE:
        setEditFuelSupplier(action.fuelSupplierItem.fuelSupplier);
        return {
          ...state,
          isDeactivateBoxVisible: true,
        };
      case FUEL_SUPPLIER_ACTIONS.SHOW_ACTIVATE:
        setEditFuelSupplier(action.fuelSupplierItem.fuelSupplier);
        return {
          ...state,
          isActivateBoxVisible: true,
        };
      case FUEL_SUPPLIER_ACTIONS.CONFIRM_EDIT:
        return {
          ...state,
          isEditBoxVisible: false,
        };
      case FUEL_SUPPLIER_ACTIONS.CONFIRM_DEACTIVATE:
        return {
          ...state,
          isDeactivateBoxVisible: false,
        };
      case FUEL_SUPPLIER_ACTIONS.CONFIRM_ACTIVATE:
        return {
          ...state,
          isActivateBoxVisible: false,
        };
      case FUEL_SUPPLIER_ACTIONS.SORT:
        sortByLocale(action.key);
        return state;
      case (FUEL_SUPPLIER_ACTIONS.ADD_NEW,
      FUEL_SUPPLIER_ACTIONS.EDIT,
      FUEL_SUPPLIER_ACTIONS.DROP,
      FUEL_SUPPLIER_ACTIONS.CLOSE):
        return initialReducerState;
      default:
        return initialReducerState;
    }
  };

  const initialReducerState = {
    isAddNewMode: true,
    isAddBoxVisible: false,
    isEditBoxVisible: false,
    isDeactivateBoxVisible: false,
    isActivateBoxVisible: false,
  };

  const [fuelSupplierState, dispatchReducer] = useReducer(
    fuelSupplierReducer,
    initialReducerState
  );

  const onChangeField = (
    value: any,
    field: string,
    state: any,
    setState: Function
  ) => {
    let tempObj = {
      ...state,
    };
    tempObj[field] = value;
    setState(tempObj);
  };

  const closeModal = () => {
    setIsNewFuelSupplierModal(false);
    // Reset Fuel Supplier Form
    resetFuelSupplierForm();
  };

  // Confirm Deactivate Fuel Supplier
  const confirmDeactivate = () => {
    if (editFuelSupplier?._id) {
      deactivateFuelSupplier(editFuelSupplier._id, userId)
        .then((res) => {
          dispatchReducer({ type: FUEL_SUPPLIER_ACTIONS.CONFIRM_DEACTIVATE });
          dispatch(pushToast({ type: "success", message: "Fuel supplier deactivated successfully" }));
          getFuelSuppliersData();
        })
        .catch((err) => {
          dispatch(pushToast({ type: "error", message: "Failed to deactivate fuel supplier" }));
        });
    }
  };

  const closeDeactivateModal = () => {
    dispatchReducer({ type: FUEL_SUPPLIER_ACTIONS.CLOSE });
  };

  // Confirm Activate Fuel Supplier
  const confirmActivate = () => {
    if (editFuelSupplier?._id) {
      activateFuelSupplier(editFuelSupplier._id, userId)
        .then((res) => {
          dispatchReducer({ type: FUEL_SUPPLIER_ACTIONS.CONFIRM_ACTIVATE });
          dispatch(pushToast({ type: "success", message: "Fuel supplier activated successfully" }));
          getFuelSuppliersData();
        })
        .catch((err) => {
          dispatch(pushToast({ type: "error", message: "Failed to activate fuel supplier" }));
        });
    }
  };

  const closeActivateModal = () => {
    dispatchReducer({ type: FUEL_SUPPLIER_ACTIONS.CLOSE });
  };

  // Confirm Edit Fuel Supplier
  const confirmEdit = () => {
    if (editFuelSupplier?._id) {
      updateFuelSupplier(editFuelSupplier._id, editFuelSupplierForm)
        .then((res) => {
          if (res.status) {
            throw res;
          } else {
            dispatchReducer({ type: FUEL_SUPPLIER_ACTIONS.CONFIRM_EDIT });
            dispatch(pushToast({ type: "success", message: res?.message }));
            getFuelSuppliersData();
          }
        })
        .catch((err) => {
          dispatch(pushToast({ type: "error", message: "Failed to update fuel supplier" }));
        });
    }
  };

  const closeEditModal = () => {
    dispatchReducer({ type: FUEL_SUPPLIER_ACTIONS.CLOSE });
  };

  const onCreateNewFuelSupplier = () => {
    postCreateNewSupplier(newFuelSupplierForm)
      .then((res) => {
        if (res.status) {
          throw res;
        } else {
          setIsNewFuelSupplierModal(false);
          dispatchReducer({ type: FUEL_SUPPLIER_ACTIONS.ADD_NEW });
          dispatch(pushToast({ type: "success", message: res?.message}));
          // Reset Fuel Supplier Form
          resetFuelSupplierForm();
          // Refresh the fuel suppliers list
          getFuelSuppliersData(searchText);
        }
      })
      .catch((err) => {
        if (
          err.status === 400 &&
          err.detail.trim().toLowerCase() === "duplicate key"
        ) {
          alert(
            "Please note that one of the Airport name/IATA code/ICAO code was existing!"
          );
        }
        console.log(err);
      });
  };

  const updatePortOptionsFromSuppliers = (suppliers: IFuelSupplier[]) => {
    const uniquePorts = Array.from(
      new Set(suppliers.map((supplier: IFuelSupplier) => supplier.AirportIATA))
    );
    const portOptions = uniquePorts.map((iata) => ({
        text: iata as string,
        value: iata as string,
      }));
    setPortOptions(portOptions);
  };

  const getFuelSuppliersData = async (searchText: string = '') => {
    await getFuelSuppliers(isFuelSupplier, searchText)
      .then(([isSucceed, msg, res]) => {
        // Sort the data by Airport IATA in ascending order for initial display
        const sortedRes = [...res].sort((a, b) => {
          const aValue = a.AirportIATA || "";
          const bValue = b.AirportIATA || "";
          // Handle empty values - show them at the end
          if (!aValue && !bValue) return 0;
          if (!aValue) return 1;
          if (!bValue) return -1;

          return aValue.localeCompare(bValue);
          });
        // Move items with State = 1 (inactive) to the end while preserving existing sort order
        const activeItems = sortedRes.filter(item => item.State === 0 || item.State == null);
        const inactiveItems = sortedRes.filter(item => item.State === 1);
        const finalSortedRes = [...activeItems, ...inactiveItems];
        setFuelSuppliers(finalSortedRes);
        setDisplayFuelSuppliers(finalSortedRes);
        updatePortOptionsFromSuppliers(finalSortedRes);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const searchFuelSupplier = async () => {
    await getFuelSuppliersData(searchText);
  };

  const filterFuelSuppliersByAirport = (
    suppliers: IFuelSupplier[],
    selectedAirport: string
  ) => {
    if (selectedAirport === "ALL") {
      return suppliers;
    }
    return suppliers.filter(
      (supplier) => supplier.AirportIATA === selectedAirport
    );
  };

  useEffect(() => {
    // Initial call of fuel suppliers api fetches all data
    getFuelSuppliersData("");
  }, []);

  useEffect(() => {
    setDisplayFuelSuppliers(
      filterFuelSuppliersByAirport(fuelSuppliers, airportIATA)
    );
  }, [airportIATA, fuelSuppliers]);

  // Populate edit form when editFuelSupplier changes
  useEffect(() => {
    if (editFuelSupplier) {
      setEditFuelSupplierForm({
        AirportIATA: editFuelSupplier.AirportIATA || "",
        VenderCode: editFuelSupplier.VenderCode || "",
        VendorName: editFuelSupplier.VendorName || "",
        AgentName: editFuelSupplier.AgentName || "",
        Remark: editFuelSupplier.Remark || "",
        UpdatedBy: userId,
      });
    }
  }, [editFuelSupplier, userId]);

  // Check if user has access to this page (SuperAdmin or Admin only)
  const hasAccess = checkIfHasAccess(pathname, userGroupIDs);

  // If user doesn't have access, show 404 Not Found page
  if (!hasAccess) {
    return <NotFoundPage />;
  }

  return (
    <>
      <div className={"d-flex justify-content-between"}>
        <div className={"CathaySansBd mb-4"} style={{ fontSize: "32px", lineHeight: "36px" }}>
          Fuel suppliers configurations
        </div>
      </div>

      {
        <>
          <div className={"justify-content-between appendixFilter"}>
            <CRow className="mb-3">
              <CCol sm="auto">
                <div className={"airline"} style={{ width: 462 }}>
                  <CXTextInput
                    placeholder={
                      "Search by vendor code/ vendor name/ agent name/ remark"
                    }
                    value={searchText}
                    onChange={setSearchText}
                    zIndex={1029}
                  />
                </div>
              </CCol>
              <CCol sm="auto">
                <div className={"searchButton"}>
                  <div>
                    <CXButton
                      text={"Search"}
                      onClick={() => {
                        searchFuelSupplier();
                      }}
                      fontSize={"16px"}
                      width={"100%"}
                    />
                  </div>
                </div>
              </CCol>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  flex: 1,
                }}
              >
                <Access route={pathname} access={[AccessType.CREATE]}>
                  <CCol sm="auto">
                    <CXButton
                      onClick={() => {
                        setIsNewFuelSupplierModal(true);
                      }}
                      text={"Add New supplier"}
                      outline={true}
                      fontSize={"14px"}
                    />
                  </CCol>
                </Access>
              </div>
            </CRow>
            <CRow>
              <div>
                <CCol sm={1} className="ps-0" style={{ minWidth: "190px" }}>
                  <CXDropDown
                    value={airportIATA}
                    placeholder={"Airport IATA"}
                    onChange={setAirportIATA}
                    options={[{ text: "ALL", value: "ALL" }, ...portOptions]}
                    zIndex={1000}
                  />
                </CCol>
              </div>
            </CRow>
          </div>

          <div className={"section"}>
            <FuelSupplierTable
              dispatchReducer={dispatchReducer}
              fuelSuppliers={displayFuelSuppliers}
              colNames={[
                "Airport IATA",
                "Vendor Code",
                "Vendor Name",
                "Agent Name",
                "Remark",
              ]}
              colValues={displayFuelSuppliers.map((fuelSupplier) => {
                const colValues = [
                  {
                    key: "airportIATA",
                    value: fuelSupplier?.AirportIATA || "--",
                  },
                  {
                    key: "vendorCode",
                    value: fuelSupplier?.VenderCode || "--",
                  },
                  {
                    key: "vendorName",
                    value: fuelSupplier?.VendorName || "--",
                  },
                  { key: "agentName", value: fuelSupplier?.AgentName || "--" },
                  { key: "remark", value: fuelSupplier?.Remark || "--" },
                ];
                return colValues;
              })}
              sortOrder={sortOrder}
            />
          </div>
        </>
      }

      {/* New port */}
      <Modal
        className="supplierModal"
        show={isNewFuelSupplierModal}
        onHide={closeModal}
        size="lg"
      >
        <Modal.Header className="modal-header" closeButton>
          <Modal.Title>Add new fuel supplier</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CRow>
            <CCol xs={6}>Fuel supplier information</CCol>
            <CCol xs={6}>
              <CRow>
                <CCol xs={6}>
                  <CXDropDown
                    value={newFuelSupplierForm.AirportIATA}
                    placeholder={"Airport IATA"}
                    onChange={setNewFuelSupplierForm}
                    onChangeField={onChangeField}
                    field="AirportIATA"
                    state={newFuelSupplierForm}
                    options={allPortOptions}
                    zIndex={1030}
                  />
                </CCol>
                <CCol xs={6}>
                  <CXTextInput
                    placeholder={"Vendor code"}
                    value={newFuelSupplierForm.VenderCode}
                    onChange={setNewFuelSupplierForm}
                    onChangeField={onChangeField}
                    field="VenderCode"
                    state={newFuelSupplierForm}
                    zIndex={1029}
                  />
                </CCol>
              </CRow>
              <CRow>
                <CXTextInput
                  placeholder={"Vendor name"}
                  value={newFuelSupplierForm.VendorName}
                  onChange={setNewFuelSupplierForm}
                  onChangeField={onChangeField}
                  field="VendorName"
                  state={newFuelSupplierForm}
                  zIndex={1029}
                />
              </CRow>
              <CRow>
                <CXTextInput
                  placeholder={"Agent name (Optional)"}
                  value={newFuelSupplierForm.AgentName}
                  onChange={setNewFuelSupplierForm}
                  onChangeField={onChangeField}
                  field="AgentName"
                  state={newFuelSupplierForm}
                  zIndex={1029}
                />
              </CRow>
            </CCol>
          </CRow>
          <br></br>
          <div className="border-bottom"></div>
          <br></br>
          <CRow>
            <CCol xs={6}>Remark (Optional)</CCol>
            <CCol xs={6}>
              <CRow>
                <CXTextArea
                  value={newFuelSupplierForm.Remark}
                  placeholder={"Remark"}
                  onChange={setNewFuelSupplierForm}
                  onChangeField={onChangeField}
                  field="Remark"
                  state={newFuelSupplierForm}
                  readOnly={false}
                  zIndex={1029}
                  minRows={5}
                />
              </CRow>
            </CCol>
          </CRow>
        </Modal.Body>
        <Modal.Footer className="modal-footer">
          <div style={{ bottom: "40px", right: "256px", width: "200px" }}>
            <CXButton
              outline={true}
              text={"Cancel"}
              onClick={() => closeModal()}
              fontSize={"16px"}
              width={"100%"}
            />
          </div>
          <div style={{ bottom: "40px", right: "40px", width: "200px" }}>
            <CXButton
              text={"Add"}
              onClick={() => onCreateNewFuelSupplier()}
              fontSize={"16px"}
              width={"100%"}
            />
          </div>
        </Modal.Footer>
      </Modal>

      {/* Edit Fuel Supplier Modal */}
      <Modal
        className="supplierModal"
        show={fuelSupplierState.isEditBoxVisible}
        onHide={closeEditModal}
        size="lg"
      >
        <Modal.Header className="modal-header" closeButton>
          <Modal.Title>Edit fuel supplier</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CRow>
            <CCol xs={6}>Fuel supplier information</CCol>
            <CCol xs={6}>
              <CRow>
                <CCol xs={6}>
                  <CXDropDown
                    value={editFuelSupplierForm.AirportIATA}
                    placeholder={"Airport IATA"}
                    onChange={setEditFuelSupplierForm}
                    onChangeField={onChangeField}
                    field="AirportIATA"
                    state={editFuelSupplierForm}
                    options={allPortOptions}
                    zIndex={1030}
                  />
                </CCol>
                <CCol xs={6}>
                  <CXTextInput
                    placeholder={"Vendor code"}
                    value={editFuelSupplierForm.VenderCode}
                    onChange={setEditFuelSupplierForm}
                    onChangeField={onChangeField}
                    field="VenderCode"
                    state={editFuelSupplierForm}
                    zIndex={1029}
                  />
                </CCol>
              </CRow>
              <CRow>
                <CXTextInput
                  placeholder={"Vendor name"}
                  value={editFuelSupplierForm.VendorName}
                  onChange={setEditFuelSupplierForm}
                  onChangeField={onChangeField}
                  field="VendorName"
                  state={editFuelSupplierForm}
                  zIndex={1029}
                />
              </CRow>
              <CRow>
                <CXTextInput
                  placeholder={"Agent name (Optional)"}
                  value={editFuelSupplierForm.AgentName}
                  onChange={setEditFuelSupplierForm}
                  onChangeField={onChangeField}
                  field="AgentName"
                  state={editFuelSupplierForm}
                  zIndex={1029}
                />
              </CRow>
            </CCol>
          </CRow>
          <br></br>
          <div className="border-bottom"></div>
          <br></br>
          <CRow>
            <CCol xs={6}>Remark (Optional)</CCol>
            <CCol xs={6}>
              <CRow>
                <CXTextArea
                  value={editFuelSupplierForm.Remark}
                  placeholder={"Remark"}
                  onChange={setEditFuelSupplierForm}
                  onChangeField={onChangeField}
                  field="Remark"
                  state={editFuelSupplierForm}
                  readOnly={false}
                  zIndex={1029}
                  minRows={5}
                />
              </CRow>
            </CCol>
          </CRow>
        </Modal.Body>
        <Modal.Footer className="modal-footer">
          <div className='cancel-button'>
            <CXButton
              outline={true}
              text={"Cancel"}
              onClick={closeEditModal}
              fontSize={"16px"}
              width={"100%"}
            />
          </div>
          <div className='save-button'>
            <CXButton
              text={"Save"}
              onClick={confirmEdit}
              fontSize={"16px"}
              width={"100%"}
            />
          </div>
        </Modal.Footer>
      </Modal>

      {/* Deactivate Fuel Supplier Confirmation Modal */}
      <Modal
        className="supplierModal"
        show={fuelSupplierState.isDeactivateBoxVisible}
        dialogClassName="modal-90w"
        onHide={closeDeactivateModal}
        size="xl"
      >
        <Modal.Header className="modal-header" closeButton>
          <Modal.Title>Deactivate fuel supplier</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4">Are you sure to deactivate this fuel supplier?</div>
          <CTable className="fuelSupplierTable" borderless hover>
            <CTableHead>
              <CTableRow>
                <CTableHeaderCell scope="col" className={"CathaySansLt px-0 fuelSupplierHeader"}>
                  Vendor Code
                </CTableHeaderCell>
                <CTableHeaderCell scope="col" className={"CathaySansLt px-0 fuelSupplierHeader"}>
                  Vendor Name
                </CTableHeaderCell>
                <CTableHeaderCell scope="col" className={"CathaySansLt px-0 fuelSupplierHeader"}>
                  Airport IATA
                </CTableHeaderCell>
                <CTableHeaderCell scope="col" className={"CathaySansLt px-0 fuelSupplierHeader"}>
                  Agent Name
                </CTableHeaderCell>
                <CTableHeaderCell scope="col" className={"CathaySansLt px-0 fuelSupplierHeader"}>
                  Remark
                </CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {editFuelSupplier && (
                <CTableRow>
                  <CTableDataCell className={"RobotoMonoRg"} style={{ position: "relative" }} align='middle'>
                    {editFuelSupplier.VenderCode || "--"}
                  </CTableDataCell>
                  <CTableDataCell className={"RobotoMonoRg"} style={{ position: "relative" }} align='middle'>
                    {editFuelSupplier.VendorName || "--"}
                  </CTableDataCell>
                  <CTableDataCell className={"RobotoMonoRg"} style={{ position: "relative" }} align='middle'>
                    {editFuelSupplier.AirportIATA || "--"}
                  </CTableDataCell>
                  <CTableDataCell className={"RobotoMonoRg"} style={{ position: "relative" }} align='middle'>
                    {editFuelSupplier.AgentName || "--"}
                  </CTableDataCell>
                  <CTableDataCell className={"RobotoMonoRg"} style={{ position: "relative" }} align='middle'>
                    {editFuelSupplier.Remark || "--"}
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>
        </Modal.Body>
        <Modal.Footer className="modal-footer">
          <div className='cancel-button'>
            <CXButton
              outline={true}
              text={"Cancel"}
              onClick={closeDeactivateModal}
              fontSize={"16px"}
              width={"100%"}
            />
          </div>
          <div className='save-button'>
            <CXButton
              text={"Deactivate"}
              onClick={confirmDeactivate}
              fontSize={"16px"}
              width={"100%"}
            />
          </div>
        </Modal.Footer>
      </Modal>

      {/* Activate Modal */}
      <Modal
        className="supplierModal"
        show={fuelSupplierState.isActivateBoxVisible}
        onHide={closeActivateModal}
        dialogClassName="modal-90w"
        size="xl"
      >
        <Modal.Header className="modal-header" closeButton>
          <Modal.Title>Re-activate fuel supplier</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4">Are you sure to re-activate this fuel supplier?</div>
          <CTable className="fuelSupplierTable" borderless hover>
            <CTableHead>
              <CTableRow>
                <CTableHeaderCell scope="col" className={"CathaySansLt px-0 fuelSupplierHeader"}>
                  Vendor Code
                </CTableHeaderCell>
                <CTableHeaderCell scope="col" className={"CathaySansLt px-0 fuelSupplierHeader"}>
                  Vendor Name
                </CTableHeaderCell>
                <CTableHeaderCell scope="col" className={"CathaySansLt px-0 fuelSupplierHeader"}>
                  Airport IATA
                </CTableHeaderCell>
                <CTableHeaderCell scope="col" className={"CathaySansLt px-0 fuelSupplierHeader"}>
                  Agent Name
                </CTableHeaderCell>
                <CTableHeaderCell scope="col" className={"CathaySansLt px-0 fuelSupplierHeader"}>
                  Remark
                </CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {editFuelSupplier && (
                <CTableRow>
                  <CTableDataCell className={"RobotoMonoRg"} style={{ position: "relative" }} align='middle'>
                    {editFuelSupplier.VenderCode || "--"}
                  </CTableDataCell>
                  <CTableDataCell className={"RobotoMonoRg"} style={{ position: "relative" }} align='middle'>
                    {editFuelSupplier.VendorName || "--"}
                  </CTableDataCell>
                  <CTableDataCell className={"RobotoMonoRg"} style={{ position: "relative" }} align='middle'>
                    {editFuelSupplier.AirportIATA || "--"}
                  </CTableDataCell>
                  <CTableDataCell className={"RobotoMonoRg"} style={{ position: "relative" }} align='middle'>
                    {editFuelSupplier.AgentName || "--"}
                  </CTableDataCell>
                  <CTableDataCell className={"RobotoMonoRg"} style={{ position: "relative" }} align='middle'>
                    {editFuelSupplier.Remark || "--"}
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>
        </Modal.Body>
        <Modal.Footer className="modal-footer">
          <div className='cancel-button'>
            <CXButton
              outline={true}
              text={"Cancel"}
              onClick={closeActivateModal}
              fontSize={"16px"}
              width={"100%"}
            />
          </div>
          <div className='save-button'>
            <CXButton
              text={"Confirm"}
              onClick={confirmActivate}
              fontSize={"16px"}
              width={"100%"}
            />
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FuelSupplierConfigPage;

